import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, FolderOpen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useCaseStudies } from '@/providers/ContentProvider'

export default function CaseStudiesPage() {
  const { caseStudies, isLoading, error } = useCaseStudies()
  return (
    <div className="pt-24 pb-16">
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <Badge variant="info" className="mb-4">Case Studies</Badge>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Real Results for <span className="text-gradient">Real Clients</span>
            </h1>
            <p className="text-xl text-muted-foreground">
              See how Echopeak Labs has helped founders ship AI-powered desktop
              apps — from first idea to signed release on macOS and Windows.
            </p>
          </motion.div>

          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading case studies...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-destructive">Failed to load case studies. Please try again later.</p>
            </div>
          ) : caseStudies.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col items-center justify-center py-24 text-center"
            >
              <div className="rounded-full bg-muted p-6 mb-6">
                <FolderOpen className="h-12 w-12 text-muted-foreground" />
              </div>
              <h2 className="font-display text-2xl font-bold mb-3">
                No Case Studies Yet
              </h2>
              <p className="text-muted-foreground max-w-md mb-8">
                We're currently crafting detailed case studies showcasing our work. 
                Check back soon to see real results from real clients.
              </p>
              <Button asChild>
                <Link to="/contact">
                  Get in Touch
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          ) : (
            <div className="space-y-12">
              {caseStudies.map((study, index) => (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="glass-card border-gradient overflow-hidden group">
                    <div className="grid lg:grid-cols-2">
                      <div className="relative overflow-hidden">
                        <img
                          src={study.bannerImageUrl}
                          alt={study.title}
                          className="w-full h-full object-cover min-h-[300px] group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-surface/80 to-transparent lg:hidden" />
                      </div>
                      <CardContent className="p-8 lg:p-10 flex flex-col justify-center">
                        <div className="flex items-center gap-2 mb-4">
                          <Badge variant="outline">{study.industry}</Badge>
                          <span className="text-muted-foreground text-sm">{study.company}</span>
                        </div>
                        
                        <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                          {study.title}
                        </h2>
                        
                        <p className="text-muted-foreground mb-6">{study.challenge}</p>
                        
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          {study.metrics.slice(0, 3).map((metric) => (
                            <div key={metric.label}>
                              <div className="font-display text-2xl font-bold text-primary">
                                {metric.value}
                              </div>
                              <div className="text-xs text-muted-foreground">{metric.label}</div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-6">
                          {study.tags.map((tag) => (
                            <Badge key={tag} variant="secondary">{tag}</Badge>
                          ))}
                        </div>
                        
                        <Button 
                          variant="outline" 
                          className="self-start" 
                          asChild
                        >
                          <Link 
                            to={`/case-studies/${study.id}`}
                            state={{ caseStudy: study }}
                          >
                            Read Full Story
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </CardContent>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

